import { GroupModel } from '@/lib/models/group.model';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { createGroup, deleteGroup, getGroupById, patchGroup } from '@/lib/services/group.service';
import { getOrgPolicyV1, getPoliciesByGroupIdsV1 } from '@/lib/services/policy-v1.service';
import { FC, useState } from 'react';
import { useQuery } from 'react-query';
import Drawer from '..';
import DrawerContent from '../content';
import DrawerHeader from '../header';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import RouterTabs from '@/components/router-tabs';
import GroupDetails from './group-details';
import { TabPanel } from '@/components/router-tabs/helpers';
import useMetabase from '@/hooks/use-metabase';
import GroupInsights from './group-insights';
import AdminAccessFields from './admin-access-fields';
import {
  GROUP_ADMIN_ACCESS_SAVED_TOAST,
  GROUP_UPDATED_TOAST,
  hasAdminAccessChanged,
  toAdminAccess,
} from './helpers';

import PolicyCardList from '@/components/policy-card-list';
import { DrawerProps } from '../helpers';
import { pluralize } from '@/lib/helpers';
import useViewMode from '@/hooks/use-view-mode.hook';
import { Box } from '@mui/material';
import MoreMenu from '@/components/more-menu';
import UpdateMenuItems from '@/components/more-menu/update-menu-items';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { FieldError } from '@/lib/services';
import useToast from '@/hooks/use-toast.hook';
import { Form } from '@/components/form';
import InputControl from '@/components/form/input-control';
import DrawerButtons from '@/components/form/drawer-buttons';
import InlineToast from '@/components/toasts/inline';
import useModal from '@/hooks/use-modal.hook';
import useFeature from '@/hooks/use-feature.hook';

interface Props extends DrawerProps {
  id: string;
}

type GroupQueryResult = [GroupModel | null, PolicyModelV1[]];

export const GroupDrawer: FC<Props> = ({
  id,
  open,
  onClose,
  className,
  hideUpdateMenu,
  isTopDrawer,
}) => {
  const [groupId, setGroupId] = useState(id || null);
  const isNew = !groupId;
  const { toast, errorToast } = useToast();
  const { openModal } = useModal();
  const { canManageAdmins } = useFeature();
  const { setMode, inWriteMode } = useViewMode(isNew ? 'add' : 'view');
  const { dashboards } = useMetabase();
  const { isValid: isDashboardValid } = dashboards.userGroupLg;

  const query = useQuery<GroupQueryResult>(
    [QueryKey.GroupMetaView, groupId],
    async () => {
      const [group, groupPolicies, orgPolicy] = await Promise.all([
        getGroupById(groupId!),
        getPoliciesByGroupIdsV1([groupId!]),
        getOrgPolicyV1(),
      ]);

      if (!group) {
        return [null, []];
      }

      const policies = [orgPolicy!, ...(groupPolicies || [])];

      return [group, policies];
    },
    {
      enabled: !isNew,
    }
  );

  const { data, refetch } = query;
  const [groupData, policies = []] = data || [];

  const group = groupData || new GroupModel();
  const { name: groupName, canDelete, isExternal } = group;
  const canEditAdminAccess = isExternal && canManageAdmins;

  const formModel = new GroupModel();

  const handleRefresh = () => {
    refetchQuery([QueryKey.GroupsDataset, QueryKey.GroupsList, QueryKey.GroupsNonSyncOptions]);
  };

  const handleChange = (group?: GroupModel) => {
    setGroupId(group?.id || '');
    refetch();
    handleRefresh();
  };

  const handleCancel = () => {
    if (isNew) {
      onClose(true);
      return;
    }
    setMode('view');
    loadData(group || new GroupModel());
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleDelete = () => {
    const { userCount = 0 } = group;
    const hasUsers = !!userCount;

    openModal('delete', {
      title: 'Delete group',
      content: (
        <>
          Are you sure you want to delete {groupName}?
          {hasUsers && (
            <Box mt={2}>
              <InlineToast
                message={`${userCount} ${pluralize(
                  userCount,
                  'End User'
                )} will lose access to the group`}
                level="warning"
              />
            </Box>
          )}
        </>
      ),
      onClose: (confirm: boolean) => {
        if (confirm) {
          void deleteGroup(groupId!).then(({ deleted, error }) => {
            if (deleted) {
              setGroupId('');
              onClose(true);
              toast('The group was deleted');
              handleRefresh();
              handleChange();
              return;
            }

            errorToast(error || 'The group was not deleted');
          });
        }
      },
    });
  };

  const formHandle = useFormHandle(
    {
      initialValues: formModel,
      validationSchema: formModel.schema,
      validateOnChange: !isNew,
      onSubmit: async (group, { resetForm, setFieldError }) => {
        const setFieldErrors = (fieldErrors: FieldError[]) => {
          fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
        };

        if (!groupId) {
          const { created, fieldErrors, error, model } = await createGroup(group);

          if (fieldErrors) {
            setFieldErrors(fieldErrors);
            return;
          }

          if (error || !created) {
            errorToast(error || 'The group was not created');
            return;
          }

          resetForm({ values: group });
          toast('The group was created');
          setGroupId(model?.id || '');
          handleChange(model as GroupModel);
          setMode('view');
        } else {
          const adminAccess = canEditAdminAccess
            ? toAdminAccess(group.adminRole, group.adminConversationAccess)
            : undefined;
          const { patched, fieldErrors, error } = await patchGroup(group, adminAccess);

          if (fieldErrors?.length) {
            setFieldErrors(fieldErrors);
            return;
          }

          if (error || !patched) {
            errorToast(error || 'The group was not updated');
            return;
          }

          // The server reconciles Admin Users in the background, so a changed mapping is not live yet
          const isAdminAccessChanged =
            adminAccess !== undefined &&
            hasAdminAccessChanged(groupData || new GroupModel(), group);

          resetForm({ values: group });
          toast(isAdminAccessChanged ? GROUP_ADMIN_ACCESS_SAVED_TOAST : GROUP_UPDATED_TOAST);
          handleChange(group);
          setMode('view');
        }
      },
    },
    { lockTarget: 'drawer' }
  );
  const { handleSubmit, canSubmit, isSubmitting, loadData } = formHandle;

  const panels: TabPanel[] = [
    {
      label: 'Group Details',
      icon: 'info-circle',
      path: 'details',
      component: <GroupDetails group={group} />,
    },
    {
      label: 'Policies',
      icon: 'policy',
      path: 'policies',
      component: <PolicyCardList policies={policies} />,
      disabled: isNew,
    },

    ...((isDashboardValid
      ? [
          {
            label: 'Insights',
            icon: 'bar-chart',
            path: 'insights',
            component: <GroupInsights name={groupName} />,
            disabled: isNew,
          },
        ]
      : []) as TabPanel[]),
  ];

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query} size="large">
      <DrawerHeader
        isTopDrawer={isTopDrawer}
        onClose={onClose}
        menu={
          <MoreMenu menuId="group-menu">
            {!isNew && !hideUpdateMenu && (
              <UpdateMenuItems
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={!inWriteMode}
                canDelete={canDelete && !inWriteMode}
                deleteSubtitle={!canDelete ? 'You cannot delete a group that is in use' : ''}
              />
            )}
          </MoreMenu>
        }
      >
        {isNew ? 'Add Group' : groupName}
      </DrawerHeader>
      <DrawerContent>
        {!inWriteMode ? (
          <RouterTabs variant="drawer" panels={panels} />
        ) : (
          <Form formHandle={formHandle} data={isNew ? undefined : group}>
            <InputControl name="name" label="Name" formHandle={formHandle} autoFocus />

            <InputControl
              name="description"
              label="Description"
              type="textarea"
              formHandle={formHandle}
            />

            <AdminAccessFields group={group} formHandle={formHandle} />

            <DrawerButtons
              submit
              cancel
              isNew={isNew}
              canSubmit={canSubmit}
              canCancel={!isSubmitting}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              formHandle={formHandle}
            />
          </Form>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default GroupDrawer;
